import PortalShell from "../../components/PortalShell";
import MonthBar from "../../components/MonthBar";
import { useAuth } from "../../lib/auth";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  Target, ClipboardCheck, TrendingUp, ArrowRight, Camera, Lock, Clock,
  CheckCircle2, Loader2,
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar } from "recharts";

/**
 * Overview: what to do now, and how the marking is going.
 *
 * **Rebuilt on the assignment system** (24 Oct 2026). It used to read
 * `/progress/me`, which measures the multiple-choice practice quiz, and every
 * tile on it linked to the quiz, the question bank, the AI tutor or Book a
 * Lesson. All four of those are now switched off, so the page was a set of
 * buttons that bounced straight back here above four metrics that would read
 * zero for ever - the quiz had no attempts and can no longer be reached.
 *
 * Everything here is now the real work: worksheets set by the tutor, marks
 * from real mark schemes, and the dates a worksheet closes on. It also loads
 * `/my/assignments`, which is what unlocks today's worksheet - this is the
 * page a child lands on, so it is the right place for the day to turn over.
 */

const pretty = (iso) => {
  if (!iso) return "";
  const d = new Date(String(iso).slice(0, 10) + "T12:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

export default function StudentDashboard() {
  const { user } = useAuth();
  const [work, setWork] = useState(null);
  const [rows, setRows] = useState(null);
  const [soon, setSoon] = useState([]);

  useEffect(() => {
    api.get("/progress/work").then((r) => setWork(r.data)).catch(() => setWork({}));
    api.get("/my/assignments")
      .then((r) => { setRows(r.data?.assignments || []); setSoon(r.data?.coming_next || []); })
      .catch(() => setRows([]));
  }, []);

  const s = work?.summary || {};
  const todo = (rows || []).filter((r) => r.status !== "submitted" && !r.closed);
  const marked = s.marked || {};

  return (
    <PortalShell role="student" title={`Hello, ${user?.name?.split(" ")[0] || "Student"}.`}
      subtitle="What to do now, and how it is going.">

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Metric icon={<ClipboardCheck />} label="To do now" value={todo.length} testid="metric-todo" />
        <Metric icon={<CheckCircle2 />} label="Handed in" value={s.handed_in ?? 0} testid="metric-handed" />
        <Metric icon={<Target />} label="Marks so far"
          value={marked.available ? `${Math.round(marked.awarded)}/${Math.round(marked.available)}` : "—"}
          testid="metric-marks" />
        <Metric icon={<TrendingUp />} label="Average"
          value={marked.pct != null ? `${marked.pct}%` : "—"} testid="metric-average" />
      </div>

      {/* How far through the month they are, directly above what is open -
          the bar is only motivating if the thing it asks for is the next
          thing on the page. */}
      <MonthBar />

      {/* To do now. The point of the page: a child should not have to go
          looking for what is open. */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl brand-navy">To do now</h3>
          <Link to="/student/assignments"
            className="text-xs gold-text inline-flex items-center gap-1 font-medium">
            All my assignments <ArrowRight size={13} />
          </Link>
        </div>

        {rows === null && (
          <p className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 size={15} className="animate-spin" /> Loading…
          </p>
        )}
        {rows !== null && todo.length === 0 && (
          <p className="text-sm text-slate-500">
            Nothing waiting. {soon.length > 0
              ? "Your next worksheet unlocks soon — see below."
              : "Your tutor will put work here."}
          </p>
        )}

        <div className="space-y-2">
          {todo.map((a) => (
            <Link key={a.id} to="/student/assignments"
              className="block rounded-lg border border-slate-200 hover:bg-slate-50/70 px-4 py-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex-1 min-w-[150px]">
                  <span className="block font-medium text-slate-900">{a.title}</span>
                  <span className="block text-xs text-slate-500 capitalize">
                    {a.kind} · {a.question_count} questions
                    {a.timed ? ` · ${Math.round((a.time_limit_seconds || 0) / 60)} min` : ""}
                  </span>
                  {a.closes_after && (
                    <span className="block text-xs text-amber-700 font-medium mt-0.5">
                      closes after {pretty(a.closes_after)}
                    </span>
                  )}
                </span>
                <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                  <Camera size={12} /> {a.photographed_count} of {a.question_count} photographed
                </span>
                {(a.answered_count > 0 || a.photographed_count > 0)
                  ? <span className="text-xs text-blue-700 font-semibold inline-flex items-center gap-1"><Clock size={12} /> carry on</span>
                  : <span className="text-xs text-amber-700 font-semibold inline-flex items-center gap-1"><Clock size={12} /> start</span>}
              </div>
            </Link>
          ))}
        </div>

        {/* Locked, and deliberately shown. A child with a reason to come back
            tomorrow is the entire purpose of the drip feed; the questions
            themselves do not exist yet, so there is nothing to read ahead. */}
        {soon.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-200">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">
              Coming up
            </p>
            <div className="space-y-1.5">
              {soon.map((x, i) => (
                <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <Lock size={13} className="text-slate-400 shrink-0" />
                  <span className="text-slate-500 flex-1 min-w-[140px]">{x.title}</span>
                  <span className="text-xs text-slate-400">
                    {x.days_away === 1 ? "unlocks tomorrow" : `unlocks in ${x.days_away} days`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-xl brand-navy">How you are doing</h3>
            <span className="text-xs uppercase tracking-widest gold-text">Marked work</span>
          </div>
          {(s.history || []).length > 1 ? (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={s.history}>
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                  <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={11} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Line type="monotone" dataKey="pct" stroke="#D4AF37" strokeWidth={2.5}
                    dot={{ r: 4, fill: "#0A1128" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              {(s.history || []).length === 1
                ? "One piece of work marked so far — the line starts once there are two."
                : "Hand in and mark a worksheet, and your progress will show here."}
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-serif text-xl brand-navy mb-6">Topics to work on</h3>
          {(s.topics || []).length ? (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                {/* Weakest first — the list is already sorted that way, and
                    the five that need work are more use than twenty that do
                    not fit on the page. */}
                <BarChart data={(s.topics || []).filter((t) => t.pct != null).slice(0, 5)}
                  layout="vertical">
                  <XAxis type="number" domain={[0, 100]} stroke="#94a3b8" fontSize={11} />
                  <YAxis type="category" dataKey="topic" stroke="#94a3b8" fontSize={11} width={110} />
                  <Tooltip />
                  <Bar dataKey="pct" fill="#0A1128" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              Topic strengths appear once some of your work has been marked.
            </p>
          )}
          <Link to="/student/analytics"
            className="mt-4 text-xs gold-text inline-flex items-center gap-1 font-medium">
            My analytics <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </PortalShell>
  );
}

function Metric({ icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-md bg-slate-50 flex items-center justify-center brand-navy">{icon}</div>
        <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
      </div>
      <div className="font-serif text-3xl brand-navy">{value}</div>
    </div>
  );
}
